/** Shared stroke sampling + crayon rendering helpers used by WritingSketchCanvas. */

export function normalizeClientPoint(clientX, clientY, rect) {
  if (!rect || !rect.width || !rect.height) return null;
  return {
    x: (Number(clientX) - rect.left) / rect.width,
    y: (Number(clientY) - rect.top) / rect.height,
  };
}

export function appendSampledPoint(points, next, minDistancePx, viewport) {
  if (!next) return points;
  if (!points.length) return [next];
  const last = points[points.length - 1];
  const dx = (next.x - last.x) * (viewport?.width || 1);
  const dy = (next.y - last.y) * (viewport?.height || 1);
  if (Math.hypot(dx, dy) < minDistancePx) return points;
  return [...points, next];
}

/**
 * Crayon feel: a slow, careful segment lays down a fat line; a quick flick
 * thins out a little. Width is derived from sampled segment length (points
 * arrive per pointer event, so length ≈ speed) and smoothed so it never
 * flickers. Returns one width per point, in canvas px.
 */
export function crayonWidths(points, viewportPx, base = 10, { min = 0.72, max = 1.12 } = {}) {
  const n = points?.length || 0;
  if (!n) return [];
  const w = viewportPx?.width || 1;
  const h = viewportPx?.height || 1;
  const raw = new Array(n).fill(base);
  for (let i = 1; i < n; i += 1) {
    const dx = (points[i].x - points[i - 1].x) * w;
    const dy = (points[i].y - points[i - 1].y) * h;
    const speed = Math.hypot(dx, dy); // px per sample
    // 2px → fat, 14px+ → thin
    const t = Math.min(1, Math.max(0, (speed - 2) / 12));
    raw[i] = base * (max - (max - min) * t);
  }
  raw[0] = raw[1] ?? base;
  const out = new Array(n);
  let acc = raw[0];
  for (let i = 0; i < n; i += 1) {
    acc = acc * 0.72 + raw[i] * 0.28;
    out[i] = acc;
  }
  return out;
}

/** Bounding box of a stroke list in normalized space (or null). */
export function strokeBounds(strokes) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const stroke of strokes || []) {
    for (const p of stroke.points || []) {
      if (p.x < minX) minX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.x > maxX) maxX = p.x;
      if (p.y > maxY) maxY = p.y;
    }
  }
  if (!Number.isFinite(minX)) return null;
  return { minX, minY, maxX, maxY, midX: (minX + maxX) / 2, midY: (minY + maxY) / 2 };
}
